import React, { useEffect, useState } from "react";
import { Search, Eye, Download, Phone, MapPin, Calendar, Clock, CheckCircle, RefreshCw } from "lucide-react";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { toast } from "sonner";
import { api, mediaUrl } from "../../services/api";
import { formatPrice } from "../../utils/formatPrice";
import { StatusBadge, STATUS_FLOW, ORDER_STATUS } from "../../utils/orderStatus";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "../../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";

const STATUSES = ["pending", "confirmed", "preparing", "shipped", "delivered", "cancelled"];

export default function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selected, setSelected] = useState(null);
  const [newStatus, setNewStatus] = useState("");
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(false);

  const load = () => {
    setLoading(true);
    const params = {};
    if (search) params.search = search;
    if (statusFilter !== "all") params.status = statusFilter;
    api.get("/admin/orders", { params })
      .then((r) => setOrders(r.data || []))
      .catch((err) => console.error("Error loading orders:", err))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter]);
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [search]);

  const openDetail = (o) => {
    setSelected(o);
    setNewStatus(o.status);
    setNotes(o.notes || "");
  };

  const updateStatus = async () => {
    if (!selected) return;
    try {
      await api.put(`/admin/orders/${selected.id}/status`, { status: newStatus });
      toast.success(`Statut mis à jour : ${ORDER_STATUS[newStatus]?.label || newStatus}`);
      load();
      setSelected({ ...selected, status: newStatus });
    } catch {
      toast.error("Erreur lors de la mise à jour du statut");
    }
  };

  const saveNotes = async () => {
    if (!selected) return;
    try {
      await api.put(`/admin/orders/${selected.id}/notes`, { notes });
      toast.success("Note interne enregistrée");
    } catch {
      toast.error("Erreur lors de l'enregistrement de la note");
    }
  };

  const exportCsv = () => {
    const rows = [["N° Commande", "Cliente", "Téléphone", "Wilaya", "Commune", "Total DZD", "Statut", "Date"]];
    orders.forEach((o) => {
      rows.push([
        o.order_number,
        o.customer_name,
        o.customer_phone,
        o.wilaya,
        o.commune || "",
        o.total,
        ORDER_STATUS[o.status]?.label || o.status,
        o.created_at?.slice(0, 10),
      ]);
    });
    const csv = rows.map((r) => r.map((c) => `"${c ?? ""}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `commandes-thequeen-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
  };

  return (
    <div className="space-y-6">
      
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-6 border-b border-[#EAE8E4]">
        <div>
          <span className="text-[11px] font-mono uppercase tracking-[0.2em] text-[#D97706] font-semibold">
            Module CRM &amp; Expéditions
          </span>
          <h1 className="font-display text-[#111111] text-3xl sm:text-4xl mt-1">
            Gestion des Commandes <span className="text-[#888888] text-2xl font-normal">({orders.length})</span>
          </h1>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={load}
            className="p-2.5 rounded-full border border-[#EAE8E4] bg-white hover:bg-[#FAF8F5] transition-colors"
            title="Rafraîchir"
          >
            <RefreshCw size={15} className={`text-[#555555] ${loading ? "animate-spin" : ""}`} />
          </button>
          <button
            onClick={exportCsv}
            className="btn-queen-secondary text-xs h-10 px-4 flex items-center gap-2 bg-white"
          >
            <Download size={14} />
            <span>Exporter CSV</span>
          </button>
        </div>
      </div>

      {/* Filter and Search Bar */}
      <div className="flex flex-wrap gap-3">
        <div className="flex items-center gap-2 bg-white border border-[#EAE8E4] rounded-full px-4 flex-1 min-w-[240px] shadow-sm" style={{ height: 44 }}>
          <Search size={16} className="text-[#999999]" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher (N° commande, nom client, téléphone, wilaya)..."
            className="flex-1 bg-transparent outline-none text-xs sm:text-sm font-body text-[#111111] placeholder-[#999999]"
            data-testid="order-search"
          />
        </div>

        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[200px] rounded-full border-[#EAE8E4] bg-white shadow-sm text-xs font-body" style={{ height: 44 }}>
            <SelectValue placeholder="Filtrer par statut" />
          </SelectTrigger>
          <SelectContent className="bg-white border border-[#EAE8E4] rounded-xl shadow-xl">
            <SelectItem value="all">Tous les statuts ({orders.length})</SelectItem>
            {STATUSES.map((s) => (
              <SelectItem key={s} value={s}>
                {ORDER_STATUS[s]?.label || s}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Orders Table */}
      <div className="bg-white rounded-2xl border border-[#EAE8E4] overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-sm font-body text-left">
            <thead>
              <tr className="border-b border-[#EAE8E4] bg-[#FAF8F5] text-[11px] uppercase tracking-wider text-[#777777] font-medium">
                <th className="p-4">N° Commande</th>
                <th className="p-4">Cliente / Client</th>
                <th className="p-4">Wilaya</th>
                <th className="p-4">Total</th>
                <th className="p-4">Statut</th>
                <th className="p-4">Date</th>
                <th className="p-4 text-center">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#EAE8E4]/70">
              {orders.map((o) => (
                <tr
                  key={o.id}
                  className="hover:bg-[#FAF8F5] transition-colors cursor-pointer"
                  onClick={() => openDetail(o)}
                  data-testid={`admin-order-${o.order_number}`}
                >
                  <td className="p-4 font-mono font-medium text-[#111111]">
                    #{o.order_number}
                  </td>
                  <td className="p-4">
                    <p className="font-medium text-[#111111]">{o.customer_name}</p>
                    <p className="text-xs text-[#777777] font-mono">{o.customer_phone}</p>
                  </td>
                  <td className="p-4 text-[#555555]">
                    {o.wilaya || "Algérie"}
                  </td>
                  <td className="p-4 font-medium text-[#111111]">
                    {formatPrice(o.total)}
                  </td>
                  <td className="p-4">
                    <StatusBadge status={o.status} />
                  </td>
                  <td className="p-4 text-xs text-[#777777]">
                    {o.created_at ? format(new Date(o.created_at), "d MMM yyyy HH:mm", { locale: fr }) : "Récemment"}
                  </td>
                  <td className="p-4 text-center" onClick={(e) => e.stopPropagation()}>
                    <button
                      onClick={() => openDetail(o)}
                      className="p-2 rounded-lg hover:bg-black/5 text-[#111111] transition-colors"
                      title="Voir les détails"
                      data-testid={`view-order-${o.order_number}`}
                    >
                      <Eye size={16} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {orders.length === 0 && !loading && (
            <div className="text-center py-16 text-[#888888] font-body text-sm">
              Aucune commande trouvée.
            </div>
          )}
        </div>
      </div>

      {/* Order Detail Modal Dialog */}
      <Dialog open={!!selected} onOpenChange={(open) => !open && setSelected(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-white border border-[#EAE8E4] rounded-3xl p-6 sm:p-8">
          {selected && (
            <>
              <DialogHeader className="border-b border-[#EAE8E4] pb-4">
                <div className="flex items-center justify-between">
                  <div>
                    <span className="text-[11px] font-mono uppercase tracking-wider text-[#D97706] font-semibold">
                      Détails de la Commande CRM
                    </span>
                    <DialogTitle className="font-display text-2xl sm:text-3xl text-[#111111] mt-0.5">
                      Commande #{selected.order_number}
                    </DialogTitle>
                  </div>
                  <StatusBadge status={selected.status} />
                </div>
              </DialogHeader>

              <div className="space-y-6 pt-4">
                
                {/* Stepper */}
                <div className="flex items-center justify-between bg-[#FAF8F5] rounded-2xl p-4 border border-[#EAE8E4]">
                  {STATUS_FLOW.map((s, i) => {
                    const reached = STATUS_FLOW.indexOf(selected.status) >= i;
                    return (
                      <div key={s} className="flex flex-col items-center flex-1">
                        <div
                          className="rounded-full grid place-items-center font-mono font-medium text-xs transition-colors"
                          style={{
                            width: 28,
                            height: 28,
                            background: reached ? "#111111" : "#EAE8E4",
                            color: reached ? "#FFFFFF" : "#777777",
                          }}
                        >
                          {i + 1}
                        </div>
                        <span
                          className="text-[10px] mt-1.5 text-center font-body"
                          style={{ color: reached ? "#111111" : "#888888", fontWeight: reached ? "600" : "400" }}
                        >
                          {ORDER_STATUS[s]?.label || s}
                        </span>
                      </div>
                    );
                  })}
                </div>

                {/* Customer & Delivery Grid */}
                <div className="grid sm:grid-cols-2 gap-4 text-xs font-body">
                  <div className="bg-[#FAF8F5] border border-[#EAE8E4] rounded-2xl p-4 space-y-1.5">
                    <p className="label-eyebrow text-[#888888]">Cliente / Client</p>
                    <p className="font-medium text-sm text-[#111111]">{selected.customer_name}</p>
                    <p className="text-[#555555] font-mono flex items-center gap-1.5">
                      <Phone size={13} className="text-[#D97706]" />
                      <a href={`tel:${selected.customer_phone}`} className="hover:underline text-[#111111]">
                        {selected.customer_phone}
                      </a>
                    </p>
                    {selected.customer_email && (
                      <p className="text-[#777777]">{selected.customer_email}</p>
                    )}
                  </div>

                  <div className="bg-[#FAF8F5] border border-[#EAE8E4] rounded-2xl p-4 space-y-1.5">
                    <p className="label-eyebrow text-[#888888]">Livraison &amp; Wilaya</p>
                    <p className="font-medium text-sm text-[#111111] flex items-center gap-1">
                      <MapPin size={13} className="text-[#D97706]" />
                      {selected.wilaya} {selected.commune ? `(${selected.commune})` : ""}
                    </p>
                    <p className="text-[#555555]">{selected.address || "Adresse non renseignée"}</p>
                    {selected.delivery_notes && (
                      <p className="text-[#777777] italic mt-1 bg-white p-2 rounded-lg border border-[#EAE8E4]">
                        « {selected.delivery_notes} »
                      </p>
                    )}
                  </div>
                </div>

                {/* Items in Order */}
                <div className="bg-white border border-[#EAE8E4] rounded-2xl p-4 space-y-3 font-body">
                  <p className="label-eyebrow text-[#888888]">Articles Commandés</p>
                  
                  <div className="space-y-3 divide-y divide-[#EAE8E4]/60">
                    {Array.isArray(selected.items) && selected.items.map((it, idx) => (
                      <div key={idx} className="flex items-center gap-3 pt-2">
                        <div className="w-10 h-12 rounded-lg overflow-hidden bg-[#FAF8F5] border border-[#EAE8E4] shrink-0">
                          {it.product_image && (
                            <img src={mediaUrl(it.product_image)} alt={it.product_name} className="w-full h-full object-cover" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-[#111111] truncate">{it.product_name}</p>
                          <p className="text-xs text-[#777777]">Quantité : {it.quantity}</p>
                        </div>
                        <div className="text-sm font-semibold text-[#111111]">
                          {formatPrice(it.total_price || (it.unit_price * it.quantity))}
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="border-t border-[#EAE8E4] pt-3 space-y-1.5 text-xs">
                    <div className="flex justify-between text-[#666666]">
                      <span>Sous-total</span>
                      <span>{formatPrice(selected.subtotal)}</span>
                    </div>
                    <div className="flex justify-between text-[#666666]">
                      <span>Livraison</span>
                      <span>{selected.delivery_fee === 0 ? "Offerte" : formatPrice(selected.delivery_fee)}</span>
                    </div>
                    {selected.discount_amount > 0 && (
                      <div className="flex justify-between text-emerald-700">
                        <span>Réduction appliquée</span>
                        <span>-{formatPrice(selected.discount_amount)}</span>
                      </div>
                    )}
                    <div className="flex justify-between text-base font-semibold text-[#111111] pt-2 border-t border-[#EAE8E4]">
                      <span>Total à encaisser</span>
                      <span>{formatPrice(selected.total)}</span>
                    </div>
                  </div>
                </div>

                {/* Change Status & Save Notes */}
                <div className="bg-[#FAF8F5] border border-[#EAE8E4] rounded-2xl p-4 space-y-4">
                  <div>
                    <label className="label-eyebrow text-[#555555] block mb-2">
                      Mettre à jour l'état de la commande
                    </label>
                    <div className="flex gap-2">
                      <Select value={newStatus} onValueChange={setNewStatus}>
                        <SelectTrigger className="flex-1 bg-white border-[#EAE8E4] rounded-xl h-11 text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-white border border-[#EAE8E4] rounded-xl shadow-xl">
                          {STATUSES.map((s) => (
                            <SelectItem key={s} value={s}>
                              {ORDER_STATUS[s]?.label || s}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>

                      <button
                        type="button"
                        onClick={updateStatus}
                        className="btn-queen-primary h-11 px-5 text-xs uppercase tracking-wider font-semibold"
                        data-testid="status-update-btn"
                      >
                        Enregistrer
                      </button>
                    </div>
                  </div>

                  <div>
                    <label className="label-eyebrow text-[#555555] block mb-2">
                      Notes internes CRM (privé)
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        placeholder="Ex: Cliente contactée au téléphone, expédition confirmée..."
                        className="flex-1 bg-white border border-[#EAE8E4] rounded-xl px-3 text-xs outline-none focus:border-[#111111]"
                      />
                      <button
                        type="button"
                        onClick={saveNotes}
                        className="btn-queen-secondary h-10 px-4 text-xs bg-white"
                      >
                        Sauvegarder
                      </button>
                    </div>
                  </div>
                </div>

              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

    </div>
  );
}
